import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, CalendarClock, Mail, Inbox, Clock, Phone, Trash2, Dog, Droplets, Euro, CalendarDays, RefreshCw, Search, BellRing, Bell, BellOff, Leaf, Pencil, CheckCircle2, Clock3, MailCheck, MailX, Send, AlarmClock, AlarmClockCheck, MessageSquare, AlertCircle, BarChart3, Users, Images, Settings2, ListChecks, PhoneCall, Paperclip, Megaphone, Ticket, ShoppingBag } from "lucide-react";
import { ANNOUNCEMENT_ICONS } from "@/lib/announcementIcons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { LOGO_URL } from "@/i18n/translations";
import { startAlarm, stopAlarm, primeAudio } from "@/lib/alarm";
import api from "@/lib/api";
import EditBookingDialog from "@/components/admin/EditBookingDialog";
import GroomingSlotsDialog from "@/components/admin/GroomingSlotsDialog";
import PhoneBookingDialog from "@/components/admin/PhoneBookingDialog";
import ReminderScheduleDialog from "@/components/admin/ReminderScheduleDialog";
import MonthCalendar from "@/components/admin/MonthCalendar";
import { ManualReminderButton } from "@/components/admin/ManualReminderButton";
import { ReviewPromptDialog } from "@/components/admin/ReviewPromptDialog";
import { WinbackPanel } from "@/components/admin/WinbackPanel";
import { DevAnalytics, DevCustomers, DevGallery, DevMessaging, DevSettings, DevAllBookings } from "@/components/admin/DevPanels";
import { ShopAdmin } from "@/components/admin/ShopAdmin";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800",
  contacted: "bg-blue-100 text-blue-800",
  confirmed: "bg-emerald-100 text-emerald-800",
  done: "bg-brand-soft text-brand",
  cancelled: "bg-rose-100 text-rose-800",
  no_show: "bg-zinc-200 text-zinc-700",
};

const StatusBadge = ({ status, labels }) => (
  <Badge className={`${STATUS_COLORS[status] || ""} border-0 font-bold`}>{labels[status] || status}</Badge>
);

const StatusSelect = ({ value, onChange, options, labels, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testid} className="w-[150px] rounded-full border-brand/20 h-9 text-sm"><SelectValue /></SelectTrigger>
    <SelectContent>
      {options.map((k) => <SelectItem key={k} value={k}>{labels[k]}</SelectItem>)}
    </SelectContent>
  </Select>
);

const EmailBadge = ({ booking, type, onSend, labels }) => {
  if (!booking.email) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-brand-ink/10 text-brand-ink/50 text-[11px] font-bold px-2.5 py-0.5" data-testid={`email-none-${booking.id}`}>
        <MailX className="h-3 w-3" /> {labels.email_none}
      </span>
    );
  }
  if (booking.email_sent) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-bamboo-soft text-bamboo-deep text-[11px] font-bold px-2.5 py-0.5" data-testid={`email-sent-${booking.id}`}>
        <MailCheck className="h-3 w-3" /> {labels.email_sent_badge}
      </span>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onSend(type, booking.id)}
      data-testid={`send-email-${booking.id}`}
      className="inline-flex items-center gap-1 rounded-full bg-amber-100 hover:bg-amber-200 text-amber-800 text-[11px] font-bold px-2.5 py-0.5 transition-colors"
      title={labels.email_send_title}
    >
      <Send className="h-3 w-3" /> {labels.email_not_sent_badge}
    </button>
  );
};

const ReminderBadge = ({ booking, type, onSend, onEdit, labels, lang }) => {
  if (!booking.email) return null;
  if (booking.reminder_sent) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-bamboo-soft text-bamboo-deep text-[11px] font-bold px-2.5 py-0.5" data-testid={`reminder-sent-${booking.id}`}>
        <AlarmClockCheck className="h-3 w-3" /> {labels.reminder_sent_badge}
      </span>
    );
  }
  const when = booking.reminder_scheduled_at
    ? new Date(booking.reminder_scheduled_at).toLocaleString(lang === "el" ? "el-GR" : "en-GB", { dateStyle: "short", timeStyle: "short" })
    : labels.reminder_not_scheduled;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-brand-soft/60 text-brand text-[11px] font-bold px-2 py-0.5" data-testid={`reminder-pending-${booking.id}`}>
      <AlarmClock className="h-3 w-3" />
      <span>{labels.reminder_at}: {when}</span>
      <button
        type="button"
        onClick={() => onSend(type, booking.id)}
        title={labels.reminder_send_title}
        className="ml-1 rounded-full bg-brand text-brand-cream text-[10px] px-2 py-0.5 hover:bg-brand-hover"
        data-testid={`send-reminder-${booking.id}`}
      >
        {labels.reminder_send_now}
      </button>
      <button
        type="button"
        onClick={() => onEdit(type, booking)}
        title={labels.reminder_edit_title}
        className="rounded-full bg-white text-brand text-[10px] px-2 py-0.5 hover:bg-brand-soft border border-brand/20"
        data-testid={`edit-reminder-${booking.id}`}
      >
        <Pencil className="h-3 w-3 inline" />
      </button>
    </span>
  );
};

const SmsBadge = ({ booking, type, onResend, labels, lang }) => {
  const st = booking.sms_status;
  if (!st) return null;
  if (st === "sent") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-bamboo-soft text-bamboo-deep text-[11px] font-bold px-2.5 py-0.5" data-testid={`sms-sent-${booking.id}`}>
        <MessageSquare className="h-3 w-3" /> {labels.sms.badge_sent}
      </span>
    );
  }
  if (st === "failed") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 text-rose-700 text-[11px] font-bold px-2 py-0.5" title={booking.sms_error || ""} data-testid={`sms-failed-${booking.id}`}>
        <AlertCircle className="h-3 w-3" />
        <span>{labels.sms.badge_failed}</span>
        <button
          type="button"
          onClick={() => onResend(type, booking.id)}
          className="ml-1 rounded-full bg-rose-600 text-white text-[10px] px-2 py-0.5 hover:bg-rose-700"
          data-testid={`resend-sms-${booking.id}`}
        >
          {labels.sms.resend}
        </button>
      </span>
    );
  }
  const when = booking.sms_scheduled_at
    ? new Date(booking.sms_scheduled_at).toLocaleString(lang === "el" ? "el-GR" : "en-GB", { dateStyle: "short", timeStyle: "short" })
    : null;
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 text-sky-800 text-[11px] font-bold px-2 py-0.5" data-testid={`sms-pending-${booking.id}`}>
      <MessageSquare className="h-3 w-3" />
      <span>{labels.sms.badge_pending}{when ? `: ${when}` : ""}</span>
    </span>
  );
};

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const isDev = user?.role === "dev";
  const { t, lang } = useLang();
  const a = t.admin;
  const navigate = useNavigate();

  const [bookings, setBookings] = useState([]);
  const [selfwash, setSelfwash] = useState([]);
  const [messages, setMessages] = useState([]);
  const [query, setQuery] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [alertActive, setAlertActive] = useState(false);
  const [soundOn, setSoundOn] = useState(true);
  const [editState, setEditState] = useState({ open: false, item: null, type: null });
  const [slotsOpen, setSlotsOpen] = useState(false);
  const [phoneBookOpen, setPhoneBookOpen] = useState(false);
  const [reminderState, setReminderState] = useState({ open: false, type: null, booking: null });
  const [reviewPrompt, setReviewPrompt] = useState({ open: false, type: null, booking: null });
  const [selectedDay, setSelectedDay] = useState(null);
  const [announcement, setAnnouncement] = useState(null);
  const seenRef = useRef(null); // Set of known booking ids; null = not yet initialized
  const soundOnRef = useRef(true);

  const openEdit = (item, type) => setEditState({ open: true, item, type });
  const closeEdit = () => setEditState((s) => ({ ...s, open: false }));

  const sendConfirmation = async (type, id) => {
    try {
      await api.post(`/admin/bookings/${type}/${id}/send-confirmation`);
      toast.success(a.email_sent_toast);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || a.email_send_error);
    }
  };

  const sendReminder = async (type, id) => {
    if (!window.confirm(a.reminder_confirm_send)) return;
    try {
      await api.post(`/admin/bookings/${type}/${id}/send-reminder`);
      toast.success(a.reminder_sent_toast);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || a.email_send_error);
    }
  };

  const editReminderSchedule = (type, booking) => setReminderState({ open: true, type, booking });

  const resendSms = async (type, id) => {
    try {
      await api.post(`/admin/bookings/${type}/${id}/resend-sms`);
      toast.success(a.sms.resent_toast);
    } catch (err) {
      toast.error(err.response?.data?.detail || a.sms.send_error);
    }
    await load();
  };

  useEffect(() => { soundOnRef.current = soundOn; }, [soundOn]);

  useEffect(() => {
    if (ready && !user) navigate("/admin/login");
  }, [ready, user, navigate]);

  // Prime audio on first user interaction so alarms can play later.
  useEffect(() => {
    const onGesture = () => primeAudio();
    window.addEventListener("pointerdown", onGesture);
    window.addEventListener("keydown", onGesture);
    return () => {
      window.removeEventListener("pointerdown", onGesture);
      window.removeEventListener("keydown", onGesture);
    };
  }, []);

  const detectNew = useCallback((groomingList, selfwashList) => {
    const currentIds = [
      ...groomingList.filter((g) => g.status === "confirmed" || g.status === "pending").map((g) => `g:${g.id}`),
      ...selfwashList.map((s) => `s:${s.id}`),
    ];
    if (seenRef.current === null) {
      seenRef.current = new Set(currentIds);
      return;
    }
    const fresh = currentIds.filter((id) => !seenRef.current.has(id));
    currentIds.forEach((id) => seenRef.current.add(id));
    if (fresh.length > 0) {
      setAlertActive(true);
      if (soundOnRef.current) startAlarm();
    }
  }, []);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const [b, sw, m] = await Promise.all([
        api.get("/admin/grooming-requests"),
        api.get("/admin/selfwash-bookings"),
        api.get("/admin/contact-messages"),
      ]);
      setBookings(b.data);
      setSelfwash(sw.data);
      setMessages(m.data);
      detectNew(b.data, sw.data);
    } catch {
      toast.error("Error loading data");
    } finally {
      setRefreshing(false);
    }
  }, [detectNew]);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  // Dev → shop announcement popup (dismissed per user with "Ενημερώθηκα!")
  useEffect(() => {
    if (!user) return;
    api.get("/admin/announcements/active").then((r) => setAnnouncement(r.data.announcement)).catch(() => {});
  }, [user]);

  const ackAnnouncement = async () => {
    try { await api.post(`/admin/announcements/${announcement.id}/dismiss`); } catch { /* still close */ }
    setAnnouncement(null);
  };

  const openAttachment = async (ann) => {
    try {
      const res = await api.get(`/admin/announcements/${ann.id}/attachment`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Error"); }
  };

  // Poll for new bookings every 12s.
  useEffect(() => {
    if (!user) return;
    const id = setInterval(load, 12000);
    return () => clearInterval(id);
  }, [user, load]);

  const acknowledgeAlert = () => {
    stopAlarm();
    setAlertActive(false);
  };

  const toggleSound = () => {
    setSoundOn((v) => {
      const next = !v;
      if (!next) stopAlarm();
      return next;
    });
  };

  useEffect(() => () => stopAlarm(), []);

  const changeBookingStatus = async (id, status) => {
    await api.patch(`/admin/grooming-requests/${id}`, { status });
    setBookings((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
    if (status === "done") {
      const b = bookings.find((x) => x.id === id);
      if (b?.phone) setReviewPrompt({ open: true, type: "grooming", booking: b });
    }
    load();
  };
  const changeSwStatus = async (id, status) => {
    await api.patch(`/admin/selfwash-bookings/${id}`, { status });
    setSelfwash((prev) => prev.map((b) => (b.id === id ? { ...b, status } : b)));
    if (status === "done") {
      const b = selfwash.find((x) => x.id === id);
      if (b?.phone) setReviewPrompt({ open: true, type: "selfwash", booking: b });
    }
    load();
  };
  const delBooking = async (id) => { await api.delete(`/admin/grooming-requests/${id}`); setBookings((p) => p.filter((b) => b.id !== id)); load(); };
  const delSw = async (id) => { await api.delete(`/admin/selfwash-bookings/${id}`); setSelfwash((p) => p.filter((b) => b.id !== id)); load(); };
  const delMessage = async (id) => { await api.delete(`/admin/contact-messages/${id}`); setMessages((p) => p.filter((m) => m.id !== id)); load(); };

  const fmt = (iso) => new Date(iso).toLocaleString(lang === "el" ? "el-GR" : "en-GB", { dateStyle: "medium", timeStyle: "short" });
  const q = query.trim().toLowerCase();
  const match = (str) => !q || (str || "").toLowerCase().includes(q);

  const ARCHIVED = new Set(["done", "cancelled", "no_show"]);
  const isArchived = (b) => ARCHIVED.has(b.status);

  const activeBookings = bookings.filter((b) => !isArchived(b));
  const activeSelfwash = selfwash.filter((b) => !isArchived(b));
  const completedItems = useMemo(() => {
    const g = bookings.filter(isArchived).map((b) => ({ ...b, _type: "grooming", _dt: b.preferred_date, _tm: b.preferred_time }));
    const s = selfwash.filter(isArchived).map((b) => ({ ...b, _type: "selfwash", _dt: b.date, _tm: b.time }));
    return [...g, ...s].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
  }, [bookings, selfwash]);

  const fBookings = useMemo(() => activeBookings.filter((b) => match(b.customer_name) || match(b.phone) || match(b.pet_name)), [activeBookings, q]);
  const fSelfwash = useMemo(() => activeSelfwash.filter((b) => match(b.customer_name) || match(b.phone)), [activeSelfwash, q]);
  const fCompleted = useMemo(() => completedItems.filter((b) => match(b.customer_name) || match(b.phone) || match(b.pet_name)), [completedItems, q]);
  const fMessages = useMemo(() => messages.filter((m) => match(m.first_name) || match(m.last_name) || match(m.email) || match(m.message)), [messages, q]);

  const todayStr = (() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`; })();

  const todayItems = useMemo(() => {
    const g = bookings.filter((b) => b.preferred_date === todayStr && b.status === "confirmed").map((b) => ({ ...b, _type: "grooming", _tm: b.preferred_time }));
    const s = selfwash.filter((b) => b.date === todayStr && b.status === "confirmed").map((b) => ({ ...b, _type: "selfwash", _tm: b.time }));
    return [...g, ...s].sort((x, y) => (x._tm || "").localeCompare(y._tm || ""));
  }, [bookings, selfwash, todayStr]);

  const dayGrooming = useMemo(() => (selectedDay ? bookings.filter((b) => b.preferred_date === selectedDay && b.status !== "cancelled") : []), [selectedDay, bookings]);
  const daySelfwash = useMemo(() => (selectedDay ? selfwash.filter((b) => b.date === selectedDay && b.status !== "cancelled") : []), [selectedDay, selfwash]);

  const renderGroomingRow = (b) => (
    <div key={b.id} className={`rounded-2xl border p-5 shadow-sm ${b.coupon_code ? "bg-emerald-50/70 border-emerald-300" : "bg-white border-brand/10"}`} data-testid={`booking-${b.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading text-lg font-bold text-brand-ink">{b.customer_name}</h3>
            <StatusBadge status={b.status} labels={a.statuses} />
            {b.coupon_code && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold px-2.5 py-0.5" data-testid={`coupon-badge-${b.id}`}>
                <Ticket className="h-3 w-3" /> {a.discount_applied} {b.coupon_discount} · {b.coupon_code}
              </span>
            )}
            {isDev && <EmailBadge booking={b} type="grooming" onSend={sendConfirmation} labels={a} />}
            {isDev && <ReminderBadge booking={b} type="grooming" onSend={sendReminder} onEdit={editReminderSchedule} labels={a} lang={lang} />}
            {isDev && <SmsBadge booking={b} type="grooming" onResend={resendSms} labels={a} lang={lang} />}
            <ManualReminderButton booking={b} type="grooming" onDone={load} />
          </div>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-brand-ink/70">
            <span className="flex items-center gap-1.5"><Phone className="h-4 w-4 text-brand" /> {b.phone}</span>
            {b.email && <span className="flex items-center gap-1.5"><Mail className="h-4 w-4 text-brand" /> {b.email}</span>}
            <span className="flex items-center gap-1.5"><Dog className="h-4 w-4 text-brand" /> {b.pet_name}{b.pet_breed ? ` · ${b.pet_breed}` : ""} ({t.grooming.sizes[b.pet_size] || b.pet_size})</span>
            <span className="flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-brand" /> {b.preferred_date} · {b.preferred_time}</span>
          </div>
          {b.service && <p className="mt-2 text-sm text-brand-ink/70"><span className="font-semibold">Service:</span> {b.service}</p>}
          {b.notes && <p className="mt-1 text-sm text-brand-ink/60 italic">{`"${b.notes}"`}</p>}
          <p className="mt-2 text-xs text-brand-ink/40">{fmt(b.created_at)}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusSelect value={b.status} onChange={(v) => changeBookingStatus(b.id, v)} options={["confirmed", "done", "cancelled"]} labels={a.statuses} testid={`status-select-${b.id}`} />
          <Button data-testid={`edit-booking-${b.id}`} onClick={() => openEdit(b, "grooming")} variant="ghost" size="icon" className="rounded-full text-brand hover:bg-brand-soft/50">
            <Pencil className="h-4 w-4" />
          </Button>
          {isDev && (
            <Button data-testid={`delete-booking-${b.id}`} onClick={() => delBooking(b.id)} variant="ghost" size="icon" className="rounded-full text-rose-500 hover:bg-rose-50 hover:text-rose-600">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  const renderSelfwashRow = (b) => (
    <div key={b.id} className={`rounded-2xl border p-5 shadow-sm ${b.coupon_code ? "bg-emerald-50/70 border-emerald-300" : "bg-white border-brand/10"}`} data-testid={`selfwash-${b.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading text-lg font-bold text-brand-ink">{b.customer_name}</h3>
            <StatusBadge status={b.status} labels={a.statuses} />
            {b.coupon_code && (
              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold px-2.5 py-0.5" data-testid={`coupon-badge-${b.id}`}>
                <Ticket className="h-3 w-3" /> {a.discount_applied} {b.coupon_discount} · {b.coupon_code}
              </span>
            )}
            {b.nagayu && <span className="rounded-full bg-bamboo-soft text-bamboo-deep text-xs font-bold px-2.5 py-0.5 inline-flex items-center gap-1"><Leaf className="h-3 w-3" /> Nagayu</span>}
            <span className="rounded-full bg-brand-soft/60 text-brand text-xs font-bold px-2.5 py-0.5">€{b.total_price || b.plan_price}</span>
            {isDev && <EmailBadge booking={b} type="selfwash" onSend={sendConfirmation} labels={a} />}
            {isDev && <ReminderBadge booking={b} type="selfwash" onSend={sendReminder} onEdit={editReminderSchedule} labels={a} lang={lang} />}
            {isDev && <SmsBadge booking={b} type="selfwash" onResend={resendSms} labels={a} lang={lang} />}
            <ManualReminderButton booking={b} type="selfwash" onDone={load} />
          </div>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-brand-ink/70">
            <span className="flex items-center gap-1.5"><Phone className="h-4 w-4 text-brand" /> {b.phone}</span>
            {b.email && <span className="flex items-center gap-1.5"><Mail className="h-4 w-4 text-brand" /> {b.email}</span>}
            <span className="flex items-center gap-1.5"><Droplets className="h-4 w-4 text-brand" /> {t.grooming.sizes[b.plan] || b.plan} · {b.plan_duration}′</span>
            <span className="flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-brand" /> {b.date} · {b.time}</span>
          </div>
          {b.notes && <p className="mt-1 text-sm text-brand-ink/60 italic">{`"${b.notes}"`}</p>}
          <p className="mt-2 text-xs text-brand-ink/40">{fmt(b.created_at)}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusSelect value={b.status} onChange={(v) => changeSwStatus(b.id, v)} options={["confirmed", "done", "no_show", "cancelled"]} labels={a.statuses} testid={`sw-status-select-${b.id}`} />
          <Button data-testid={`edit-selfwash-${b.id}`} onClick={() => openEdit(b, "selfwash")} variant="ghost" size="icon" className="rounded-full text-brand hover:bg-brand-soft/50">
            <Pencil className="h-4 w-4" />
          </Button>
          {isDev && (
            <Button data-testid={`delete-selfwash-${b.id}`} onClick={() => delSw(b.id)} variant="ghost" size="icon" className="rounded-full text-rose-500 hover:bg-rose-50 hover:text-rose-600">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );

  if (!ready || !user) return null;

  return (
    <div className="min-h-screen bg-brand-soft/20">
      <header className="glass border-b border-brand/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <img src={LOGO_URL} alt="Care For Me" className="h-7 sm:h-8 w-auto" />
            <span className="hidden sm:inline font-heading text-lg font-bold text-brand-ink border-l border-brand/20 pl-3">{a.dashboard}</span>
            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${isDev ? "bg-brand-ink text-brand-cream" : "bg-bamboo-soft text-bamboo-deep"}`} data-testid="role-chip">
              {isDev ? a.role_dev : a.role_groomer}
            </span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <Button data-testid="admin-sound-toggle" onClick={toggleSound} variant="outline" size="icon" title={soundOn ? a.sound_on : a.sound_off} className={`rounded-full border-brand/30 ${soundOn ? "text-bamboo-deep" : "text-brand-ink/40"} hover:bg-brand-soft/40`}>
              {soundOn ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
            </Button>
            <Button data-testid="admin-refresh-btn" onClick={load} variant="outline" size="icon" className="rounded-full border-brand/30 text-brand hover:bg-brand-soft/40">
              <RefreshCw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            </Button>
            <Button data-testid="admin-logout-btn" onClick={() => { logout(); navigate("/"); }} variant="outline" className="rounded-full border-brand/30 text-brand hover:bg-brand-soft/40 font-bold px-2.5 sm:px-4">
              <LogOut className="h-4 w-4 sm:mr-2" /> <span className="hidden sm:inline">{a.logout}</span>
            </Button>
          </div>
        </div>
      </header>

      {alertActive && (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-brand-ink/60 backdrop-blur-sm p-5" data-testid="new-booking-alert">
          <motion.div
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: [1, 1.03, 1], opacity: 1 }}
            transition={{ scale: { repeat: Infinity, duration: 1 }, opacity: { duration: 0.2 } }}
            className="bg-white rounded-[2rem] border-4 border-brand shadow-2xl p-8 max-w-sm w-full text-center"
          >
            <span className="mx-auto grid place-items-center h-16 w-16 rounded-full bg-brand text-brand-cream mb-4 animate-sway">
              <BellRing className="h-8 w-8" />
            </span>
            <h2 className="font-heading text-3xl font-medium text-brand-ink">{a.new_booking_title}</h2>
            <p className="mt-2 text-brand-muted">{a.new_booking_body}</p>
            <Button data-testid="accept-booking-btn" onClick={acknowledgeAlert} className="mt-6 w-full rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-12 text-base">
              {a.accept}
            </Button>
          </motion.div>
        </div>
      )}

      {announcement && (
        <div className="fixed inset-0 z-[65] grid place-items-center bg-brand-ink/60 backdrop-blur-sm p-5" data-testid="announcement-popup">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-[2rem] border border-brand/15 shadow-2xl p-8 max-w-md w-full"
          >
            <span className="mx-auto grid place-items-center h-14 w-14 rounded-full bg-brand text-brand-cream mb-4" data-testid="announcement-icon">
              {React.createElement(ANNOUNCEMENT_ICONS[announcement.icon] || Megaphone, { className: "h-7 w-7" })}
            </span>
            <h2 className="font-heading text-2xl font-bold text-brand-ink text-center" data-testid="announcement-title">{announcement.title}</h2>
            <p className="mt-3 text-brand-ink/75 leading-relaxed whitespace-pre-wrap text-center" data-testid="announcement-body">{announcement.body}</p>
            {announcement.attachment && (
              <button
                type="button"
                onClick={() => openAttachment(announcement)}
                className="mt-4 mx-auto flex items-center gap-2 rounded-full bg-brand-soft/60 text-brand text-sm font-bold px-4 py-2 hover:bg-brand-soft transition-colors"
                data-testid="announcement-attachment"
              >
                <Paperclip className="h-4 w-4" /> {announcement.attachment.name}
              </button>
            )}
            <Button data-testid="announcement-ack" onClick={ackAnnouncement} className="mt-6 w-full rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold h-12 text-base">
              {a.ack_button}
            </Button>
          </motion.div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-8">
        <div className="grid gap-4 lg:grid-cols-[1fr_400px] mb-6">
          <MonthCalendar grooming={bookings} selfwash={selfwash} selectedDate={selectedDay} onDaySelect={(dStr) => setSelectedDay(dStr)} />
          <div className="rounded-2xl bg-white border border-brand/10 p-5 shadow-[0_8px_30px_rgba(134,77,99,0.06)]" data-testid="today-agenda">
            <p className="font-heading text-lg font-bold text-brand-ink flex items-center gap-2">
              <CalendarDays className="h-5 w-5 text-brand" /> {a.today_agenda}
              <span className="ml-auto rounded-full bg-brand text-brand-cream text-sm font-bold px-3 py-0.5" data-testid="today-count">{todayItems.length}</span>
            </p>
            <div className="mt-3 space-y-2 max-h-[300px] overflow-y-auto">
              {todayItems.length === 0 ? (
                <p className="text-sm text-brand-ink/50 italic py-4 text-center" data-testid="today-empty">{a.no_today}</p>
              ) : todayItems.map((b) => (
                <div key={`${b._type}-${b.id}`} className="flex items-center gap-2 rounded-xl bg-stone-alt/50 px-3 py-2" data-testid={`today-item-${b.id}`}>
                  <span className={`grid place-items-center h-7 w-7 rounded-lg ${b._type === "grooming" ? "bg-brand/15 text-brand" : "bg-bamboo-soft text-bamboo-deep"}`}>
                    {b._type === "grooming" ? <Dog className="h-3.5 w-3.5" /> : <Droplets className="h-3.5 w-3.5" />}
                  </span>
                  <span className="text-sm font-bold text-brand-ink">{b._tm}</span>
                  <span className="text-sm text-brand-ink/80 truncate">{b.customer_name}{b.pet_name ? ` (${b.pet_name})` : ""}</span>
                  <a href={`tel:${b.phone}`} className="ml-auto grid place-items-center h-8 w-8 rounded-full bg-bamboo text-white hover:bg-bamboo-hover no-a11y-underline" data-testid={`today-call-${b.id}`} aria-label="Call">
                    <PhoneCall className="h-4 w-4" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mb-6 rounded-2xl bg-white border border-brand/10 p-5 shadow-[0_8px_30px_rgba(134,77,99,0.06)]" data-testid="landing-links">
          <p className="text-xs font-bold uppercase tracking-wide text-brand-ink/50 mb-3">{a.landing_pages}</p>
          <div className="flex flex-wrap gap-3">
            {isDev && (
              <a href="/landing/grooming" target="_blank" rel="noreferrer" data-testid="btn-landing-grooming">
                <Button className="rounded-full bg-brand hover:bg-brand-hover text-brand-cream font-bold no-a11y-underline">
                  <Dog className="h-4 w-4 mr-2" /> {a.landing_grooming}
                </Button>
              </a>
            )}
            {isDev && (
              <a href="/landing/self" target="_blank" rel="noreferrer" data-testid="btn-landing-self">
                <Button className="rounded-full bg-bamboo hover:bg-bamboo-hover text-brand-cream font-bold no-a11y-underline">
                  <Droplets className="h-4 w-4 mr-2" /> {a.landing_self}
                </Button>
              </a>
            )}
            <Button
              onClick={() => setSlotsOpen(true)}
              variant="outline"
              className="rounded-full border-brand/30 text-brand hover:bg-brand-soft/50 font-bold"
              data-testid="btn-manage-slots"
            >
              <Clock3 className="h-4 w-4 mr-2" /> {a.slots.open_button}
            </Button>
            <Button
              onClick={() => setPhoneBookOpen(true)}
              className="rounded-full bg-brand-ink hover:bg-brand-ink/90 text-brand-cream font-bold"
              data-testid="btn-phone-booking"
            >
              <Phone className="h-4 w-4 mr-2" /> {a.phone_book.open_button}
            </Button>
          </div>
        </div>

        <div className="mb-5 relative max-w-sm">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-brand/50" />
          <Input data-testid="admin-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={a.search} className="pl-10 rounded-full border-brand/20 bg-white h-11 focus-visible:ring-brand" />
        </div>

        <div className="mb-6">
          <WinbackPanel isDev={false} />
        </div>

        <Tabs defaultValue="bookings">
          <TabsList className="bg-white border border-brand/10 rounded-full p-1 flex-nowrap h-auto overflow-x-auto justify-start max-w-full">
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="rounded-full data-[state=active]:bg-brand data-[state=active]:text-brand-cream font-bold px-5 shrink-0">
              {a.bookings} ({activeBookings.length})
            </TabsTrigger>
            <TabsTrigger value="selfwash" data-testid="tab-selfwash" className="rounded-full data-[state=active]:bg-brand data-[state=active]:text-brand-cream font-bold px-5 shrink-0">
              {a.selfwash} ({activeSelfwash.length})
            </TabsTrigger>
            <TabsTrigger value="completed" data-testid="tab-completed" className="rounded-full data-[state=active]:bg-brand data-[state=active]:text-brand-cream font-bold px-5 shrink-0">
              {a.completed} ({completedItems.length})
            </TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages" className="rounded-full data-[state=active]:bg-brand data-[state=active]:text-brand-cream font-bold px-5 shrink-0">
              {a.messages} ({messages.length})
            </TabsTrigger>
            <TabsTrigger value="customers" data-testid="tab-customers-groomer" className="rounded-full data-[state=active]:bg-brand data-[state=active]:text-brand-cream font-bold px-5 shrink-0">
              <Users className="h-4 w-4 mr-1.5" /> {a.tab_customers}
            </TabsTrigger>
            <TabsTrigger value="boutique" data-testid="tab-boutique" className="rounded-full data-[state=active]:bg-brand data-[state=active]:text-brand-cream font-bold px-5 shrink-0">
              <ShoppingBag className="h-4 w-4 mr-1.5" /> {t.ashop.nav_boutique}
            </TabsTrigger>
            {isDev && (
              <TabsTrigger value="all" data-testid="tab-all" className="rounded-full data-[state=active]:bg-brand-ink data-[state=active]:text-brand-cream font-bold px-5 shrink-0">
                <ListChecks className="h-4 w-4 mr-1.5" /> {a.tab_all}
              </TabsTrigger>
            )}
            {isDev && (
              <TabsTrigger value="analytics" data-testid="tab-analytics" className="rounded-full data-[state=active]:bg-brand-ink data-[state=active]:text-brand-cream font-bold px-5 shrink-0">
                <BarChart3 className="h-4 w-4 mr-1.5" /> {a.tab_analytics}
              </TabsTrigger>
            )}
            {isDev && (
              <TabsTrigger value="customers" data-testid="tab-customers" className="rounded-full data-[state=active]:bg-brand-ink data-[state=active]:text-brand-cream font-bold px-5 shrink-0">
                <Users className="h-4 w-4 mr-1.5" /> {a.tab_customers}
              </TabsTrigger>
            )}
            {isDev && (
              <TabsTrigger value="gallery" data-testid="tab-gallery" className="rounded-full data-[state=active]:bg-brand-ink data-[state=active]:text-brand-cream font-bold px-5 shrink-0">
                <Images className="h-4 w-4 mr-1.5" /> {a.tab_gallery}
              </TabsTrigger>
            )}
            {isDev && (
              <TabsTrigger value="messaging" data-testid="tab-messaging" className="rounded-full data-[state=active]:bg-brand-ink data-[state=active]:text-brand-cream font-bold px-5 shrink-0">
                <MessageSquare className="h-4 w-4 mr-1.5" /> {a.tab_messaging}
              </TabsTrigger>
            )}
            {isDev && (
              <TabsTrigger value="settings" data-testid="tab-settings" className="rounded-full data-[state=active]:bg-brand-ink data-[state=active]:text-brand-cream font-bold px-5 shrink-0">
                <Settings2 className="h-4 w-4 mr-1.5" /> {a.tab_settings}
              </TabsTrigger>
            )}
          </TabsList>

          {/* Grooming */}
          <TabsContent value="bookings" className="mt-6">
            {fBookings.length === 0 ? (
              <p className="text-center text-brand-ink/50 py-16" data-testid="no-bookings">{a.no_bookings}</p>
            ) : (
              <div className="grid gap-4" data-testid="bookings-list">
                {fBookings.map(renderGroomingRow)}
              </div>
            )}
          </TabsContent>

          {/* Self-service */}
          <TabsContent value="selfwash" className="mt-6">
            {fSelfwash.length === 0 ? (
              <p className="text-center text-brand-ink/50 py-16" data-testid="no-selfwash">{a.no_selfwash}</p>
            ) : (
              <div className="grid gap-4" data-testid="selfwash-list">
                {fSelfwash.map(renderSelfwashRow)}
              </div>
            )}
          </TabsContent>

          {/* Completed / archived */}
          <TabsContent value="completed" className="mt-6">
            {fCompleted.length === 0 ? (
              <p className="text-center text-brand-ink/50 py-16" data-testid="no-completed">{a.no_completed}</p>
            ) : (
              <div className="grid gap-4" data-testid="completed-list">
                {fCompleted.map((b) => (
                  <div key={`${b._type}-${b.id}`} className="rounded-2xl bg-white/70 border border-brand/10 p-5 shadow-sm" data-testid={`completed-${b._type}-${b.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-[11px] uppercase tracking-wide font-bold px-2.5 py-0.5 rounded-full ${b._type === "grooming" ? "bg-brand-soft text-brand" : "bg-bamboo-soft text-bamboo-deep"}`}>
                            {b._type === "grooming" ? a.grooming_type : a.selfwash_type}
                          </span>
                          <h3 className="font-heading text-lg font-bold text-brand-ink">{b.customer_name}</h3>
                          <StatusBadge status={b.status} labels={a.statuses} />
                          {b.nagayu && <span className="rounded-full bg-bamboo-soft text-bamboo-deep text-xs font-bold px-2.5 py-0.5 inline-flex items-center gap-1"><Leaf className="h-3 w-3" /> Nagayu</span>}
                        </div>
                        <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-brand-ink/70">
                          <span className="flex items-center gap-1.5"><Phone className="h-4 w-4 text-brand" /> {b.phone}</span>
                          <span className="flex items-center gap-1.5"><CalendarClock className="h-4 w-4 text-brand" /> {b._dt} · {b._tm}</span>
                          {b._type === "grooming" && b.pet_name && <span className="flex items-center gap-1.5"><Dog className="h-4 w-4 text-brand" /> {b.pet_name}</span>}
                          {b._type === "selfwash" && <span className="flex items-center gap-1.5"><Euro className="h-4 w-4 text-brand" /> €{b.total_price || b.plan_price}</span>}
                        </div>
                        {b.notes && <p className="mt-1 text-sm text-brand-ink/60 italic">{`"${b.notes}"`}</p>}
                        <p className="mt-2 text-xs text-brand-ink/40">{fmt(b.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="grid place-items-center h-9 w-9 rounded-full bg-brand-soft text-brand"><CheckCircle2 className="h-4 w-4" /></span>
                        {isDev && (
                          <Button
                            data-testid={`del-completed-${b._type}-${b.id}`}
                            onClick={() => b._type === "grooming" ? delBooking(b.id) : delSw(b.id)}
                            variant="ghost"
                            size="icon"
                            className="rounded-full text-rose-500 hover:bg-rose-50 hover:text-rose-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Messages */}
          <TabsContent value="messages" className="mt-6">
            {fMessages.length === 0 ? (
              <p className="text-center text-brand-ink/50 py-16" data-testid="no-messages">{a.no_messages}</p>
            ) : (
              <div className="grid gap-4 md:grid-cols-2" data-testid="messages-list">
                {fMessages.map((m) => (
                  <div key={m.id} className="rounded-2xl bg-white border border-brand/10 p-5 shadow-sm" data-testid={`message-${m.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-heading text-lg font-bold text-brand-ink">{m.first_name} {m.last_name || ""}</h3>
                        <a href={`mailto:${m.email}`} className="text-sm text-brand font-semibold">{m.email}</a>
                      </div>
                      <Button data-testid={`delete-message-${m.id}`} onClick={() => delMessage(m.id)} variant="ghost" size="icon" className="rounded-full text-rose-500 hover:bg-rose-50 hover:text-rose-600">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="mt-3 text-sm text-brand-ink/75 leading-relaxed">{m.message}</p>
                    <p className="mt-3 text-xs text-brand-ink/40">{fmt(m.created_at)}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Customers (shared CRM, no bulk send for shop) */}
          <TabsContent value="customers" className="mt-6">
            <DevCustomers canSend={false} />
          </TabsContent>

          {/* Boutique (groomer: orders, supplier orders, availability) */}
          <TabsContent value="boutique" className="mt-6">
            <ShopAdmin />
          </TabsContent>

          {isDev && (
            <TabsContent value="all" className="mt-6"><DevAllBookings grooming={bookings} selfwash={selfwash} /></TabsContent>
          )}
          {isDev && (
            <TabsContent value="analytics" className="mt-6"><DevAnalytics /></TabsContent>
          )}
          {isDev && (
            <TabsContent value="customers" className="mt-6"><DevCustomers /></TabsContent>
          )}
          {isDev && (
            <TabsContent value="gallery" className="mt-6"><DevGallery /></TabsContent>
          )}
          {isDev && (
            <TabsContent value="messaging" className="mt-6"><DevMessaging /></TabsContent>
          )}
          {isDev && (
            <TabsContent value="settings" className="mt-6"><DevSettings /></TabsContent>
          )}
        </Tabs>
      </main>

      <EditBookingDialog
        open={editState.open}
        onOpenChange={(v) => setEditState((s) => ({ ...s, open: v }))}
        item={editState.item}
        type={editState.type}
        onSaved={(updated) => {
          if (editState.type === "grooming") {
            setBookings((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
          } else {
            setSelfwash((prev) => prev.map((b) => (b.id === updated.id ? updated : b)));
          }
          load();
        }}
      />

      <GroomingSlotsDialog open={slotsOpen} onOpenChange={setSlotsOpen} />
      <PhoneBookingDialog open={phoneBookOpen} onOpenChange={setPhoneBookOpen} onCreated={load} />
      <ReminderScheduleDialog
        open={reminderState.open}
        onOpenChange={(v) => setReminderState((s) => ({ ...s, open: v }))}
        booking={reminderState.booking}
        type={reminderState.type}
        onSaved={load}
      />
      <ReviewPromptDialog
        state={reviewPrompt}
        onClose={(sent) => { setReviewPrompt({ open: false, type: null, booking: null }); if (sent) load(); }}
      />

      <Dialog open={!!selectedDay} onOpenChange={(v) => !v && setSelectedDay(null)}>
        <DialogContent className="bg-brand-cream max-w-2xl rounded-[1.75rem] p-0 overflow-y-auto max-h-[92vh]" data-testid="day-bookings-dialog">
          <div className="bg-brand text-brand-cream px-6 pt-6 pb-5">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-brand-cream flex items-center gap-2">
                <CalendarDays className="h-5 w-5" /> {a.day_bookings} · {selectedDay}
              </DialogTitle>
            </DialogHeader>
          </div>
          <div className="px-6 py-6 space-y-4">
            {dayGrooming.length === 0 && daySelfwash.length === 0 ? (
              <p className="text-center text-brand-ink/50 py-8 italic" data-testid="day-empty">{a.no_bookings}</p>
            ) : (
              <>
                {dayGrooming.map(renderGroomingRow)}
                {daySelfwash.map(renderSelfwashRow)}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

